<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import McpServerCard from './McpServerCard.vue';
import McpServerDialog from './McpServerDialog.vue';
import McpServerForm from './McpServerForm.vue';
import DeleteMcpServerDialog from './DeleteMcpServerDialog.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const showCreateForm = ref(false);
const showEditForm = ref(false);
const showDeleteDialog = ref(false);
const selectedServer = ref(null);
const isSubmittingEdit = ref(false);

const mcpServers = computed(() => {
  const allServers = getters['mcpServers/getMcpServers'].value || [];
  return allServers;
});

const uiFlags = computed(() => getters['mcpServers/getUIFlags'].value || {});

const isLoading = computed(() => uiFlags.value.isFetching);
const isEmpty = computed(() => mcpServers.value.length === 0);

const handleCreate = () => {
  selectedServer.value = null;
  showCreateForm.value = true;
};

const handleEdit = server => {
  selectedServer.value = server;
  showEditForm.value = true;
};

const handleDelete = server => {
  selectedServer.value = server;
  showDeleteDialog.value = true;
};

const handleTestConnection = async server => {
  try {
    await store.dispatch('mcpServers/testConnection', server.id);
    useAlert(t('INTEGRATION_SETTINGS.MCP_SERVERS.MESSAGES.CONNECTION_SUCCESS'));
  } catch (error) {
    // Use more specific error message if available
    const errorMessage =
      error.message ||
      error.response?.data?.error ||
      t('INTEGRATION_SETTINGS.MCP_SERVERS.MESSAGES.CONNECTION_ERROR');
    useAlert(errorMessage);
  }
};

const handleFormSubmit = async formData => {
  if (isSubmittingEdit.value) return; // Prevent double submission

  isSubmittingEdit.value = true;

  try {
    await store.dispatch('mcpServers/update', {
      id: selectedServer.value.id,
      ...formData,
    });
    useAlert(t('INTEGRATION_SETTINGS.MCP_SERVERS.MESSAGES.UPDATE_SUCCESS'));
    showEditForm.value = false;
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.MCP_SERVERS.MESSAGES.UPDATE_ERROR'));
  } finally {
    isSubmittingEdit.value = false;
  }
};

const handleDeleteConfirm = async () => {
  try {
    await store.dispatch('mcpServers/delete', selectedServer.value.id);
    useAlert(t('INTEGRATION_SETTINGS.MCP_SERVERS.MESSAGES.DELETE_SUCCESS'));
    showDeleteDialog.value = false;
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.MCP_SERVERS.MESSAGES.DELETE_ERROR'));
  }
};

const isTestingConnection = serverId => {
  return getters['mcpServers/isTestingConnection'].value(serverId);
};

// Lifecycle
onMounted(() => {
  store.dispatch('mcpServers/get');
});
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.MCP_SERVERS.HEADER')"
    :button-label="$t('INTEGRATION_SETTINGS.MCP_SERVERS.ADD_NEW')"
    @click="handleCreate"
    @close="showCreateForm = false"
  >
    <template #action>
      <McpServerDialog v-if="showCreateForm" @close="showCreateForm = false" />
    </template>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <div
        class="w-16 h-16 mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
      >
        <Icon name="i-lucide-server" class="w-8 h-8 text-slate-400" />
      </div>
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
        {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-slate-600 dark:text-slate-400 mb-6 max-w-md">
        {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.EMPTY_STATE.SUBTITLE') }}
      </p>
      <Button
        :label="$t('INTEGRATION_SETTINGS.MCP_SERVERS.EMPTY_STATE.ACTION')"
        icon="i-lucide-plus"
        @click="handleCreate"
      />
    </div>

    <!-- Server List -->
    <div v-else class="space-y-4">
      <McpServerCard
        v-for="server in mcpServers"
        :key="server.id"
        :server="server"
        :is-testing-connection="isTestingConnection(server.id)"
        @test="handleTestConnection(server)"
        @edit="handleEdit(server)"
        @delete="handleDelete(server)"
      />
    </div>

    <McpServerForm
      v-if="showEditForm"
      mode="edit"
      :server="selectedServer"
      :is-submitting="isSubmittingEdit"
      @submit="handleFormSubmit"
      @close="showEditForm = false"
    />

    <DeleteMcpServerDialog
      v-if="showDeleteDialog"
      :server="selectedServer"
      @confirm="handleDeleteConfirm"
      @close="showDeleteDialog = false"
    />
  </SettingsLayout>
</template>
